import {
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as AssignmentIcon,
  Cake as CakeIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Height as HeightIcon,
  MenuBook as MenuBookIcon,
  Message as MessageIcon,
  MonitorWeight as MonitorWeightIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  Restaurant as RestaurantIcon,
  Schedule as ScheduleIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CancelButton from '../components/CancelButton';
import DailyMealPlansView from '../components/DailyMealPlansView';
import Loading from '../components/Loading';
import { useClientData } from '../hooks/useClientData';
import { Meal, mealApi } from '../services/mealApi';
import { messageApi } from '../services/messageApi';
import {
  WeightMeasurement,
  weightMeasurementApi,
} from '../services/weightMeasurementApi';
import { colors } from '../theme';

const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    client,
    clientId,
    dietitian,
    appointments,
    loading,
    error,
    getCurrentDietProgram,
    showInfo,
    showError,
  } = useClientData({
    fetchAppointments: true,
    fetchDietPrograms: true,
    fetchDietitian: true,
  });

  const [programDetailOpen, setProgramDetailOpen] = useState(false);
  const [meals, setMeals] = useState<Meal[]>([]);
  const [weightMeasurements, setWeightMeasurements] = useState<
    WeightMeasurement[]
  >([]);
  const [unreadMessages, setUnreadMessages] = useState(0);



  const fetchStats = useCallback(async () => {
    if (!clientId) return;
    try {
      const [mealsData, measurementsData] = await Promise.all([
        mealApi.getMealsByClient(clientId),
        weightMeasurementApi.getMeasurementsByClient(clientId),
      ]);
      setMeals(mealsData);
      setWeightMeasurements(measurementsData);

      // Okunmamış mesaj sayısını al (sadece clientId varsa)
      if (clientId) {
        try {
          const conversations = await messageApi.getConversations(clientId);
          const totalUnread = conversations.reduce(
            (sum, conv) => sum + conv.unreadCount,
            0
          );
          setUnreadMessages(totalUnread);
        } catch (err) {
          console.error('Error fetching messages:', err);
          // Mesajlar yüklenemezse 0 olarak kalır, hata gösterme
        }
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [clientId]);

  // İstatistikleri yükle
  useEffect(() => {
    if (clientId) {
      fetchStats();
    }
  }, [clientId, fetchStats]);



  const currentProgram = getCurrentDietProgram();
  
  // Bugünün yemek planını bul
  const todayMealPlan = useMemo(() => {
    if (!currentProgram?.dailyMealPlans || currentProgram.dailyMealPlans.length === 0) {
      return null;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    
    // Tarih formatını normalize et - backend'den string olarak gelir
    const todayMealPlan = currentProgram.dailyMealPlans.find(plan => {
      if (!plan.date) return false;
      
      try {
        // String'i Date'e çevir
        const planDate = new Date(plan.date);
        if (isNaN(planDate.getTime())) return false;
        
        // Tarihleri normalize et (sadece tarih kısmını al)
        planDate.setHours(0, 0, 0, 0);
        const planDateStr = planDate.toISOString().split('T')[0];
        
        return planDateStr === todayStr;
      } catch (error) {
        console.error('Tarih karşılaştırma hatası:', error, plan.date);
        return false;
      }
    });
    
    return todayMealPlan || null;
  }, [currentProgram]);

  // İstatistikler
  const stats = useMemo(() => {
    const totalAppointments = appointments.length;
    const completedAppointments = appointments.filter(
      (apt) => apt.status === 'Tamamlandı'
    ).length;
    const upcomingAppointments = appointments.filter(
      (apt) =>
        new Date(apt.appointmentDate) > new Date() && apt.status === 'Planlandı'
    ).length;

    const latestWeight = weightMeasurements
      .filter((m) => m.weight)
      .sort(
        (a, b) =>
          new Date(b.measurementDate).getTime() -
          new Date(a.measurementDate).getTime()
      )[0];
    const previousWeight = weightMeasurements
      .filter((m) => m.weight)
      .sort(
        (a, b) =>
          new Date(b.measurementDate).getTime() -
          new Date(a.measurementDate).getTime()
      )[1];

    const weightChange =
      latestWeight && previousWeight
        ? latestWeight.weight! - previousWeight.weight!
        : 0;

    const todayMeals = meals.filter(
      (meal) =>
        new Date(meal.mealDate).toDateString() === new Date().toDateString()
    );
    const totalCaloriesToday = todayMeals.reduce(
      (sum, meal) => sum + meal.calories,
      0
    );

    const programProgress = currentProgram
      ? Math.min(
          100,
          Math.max(
            0,
            ((new Date().getTime() -
              new Date(currentProgram.startDate).getTime()) /
              (new Date(currentProgram.endDate).getTime() -
                new Date(currentProgram.startDate).getTime())) *
              100
          )
        )
      : 0;

    return {
      totalAppointments,
      completedAppointments,
      upcomingAppointments,
      latestWeight: latestWeight?.weight,
      weightChange,
      totalCaloriesToday,
      programProgress,
      recentMeals: meals
        .sort(
          (a, b) =>
            new Date(b.mealDate).getTime() - new Date(a.mealDate).getTime()
        )
        .slice(0, 5),
      recentMeasurements: weightMeasurements
        .sort(
          (a, b) =>
            new Date(b.measurementDate).getTime() -
            new Date(a.measurementDate).getTime()
        )
        .slice(0, 5),
      recentAppointments: appointments
        .sort(
          (a, b) =>
            new Date(b.appointmentDate).getTime() -
            new Date(a.appointmentDate).getTime()
        )
        .slice(0, 5),
      todayMeals,
    };
  }, [appointments, weightMeasurements, meals, currentProgram]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Veriler yükleniyor..."
      />
    );
  }

  return (
    <>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Welcome Section - Hoş geldiniz + Boy/Kilo/Yaş/Cinsiyet + Diyetisyen */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
          color: 'white',
          border: 'none',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 40% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255, 255, 255, 0.03) 20px,
                rgba(255, 255, 255, 0.03) 20px
              )
            `,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Üst satır: Hoş geldiniz (sol) + Diyetisyen (sağ) */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
              mb: client && (client.height != null || client.weight != null || client.age != null || client.gender) ? 2 : 0,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: 'white', mb: 0.5 }}
              >
                Hoş geldiniz{client ? `, ${client.firstName}` : ''}!
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}
              >
                Diyet programınızı takip edin ve hedeflerinize ulaşın
              </Typography>
            </Box>
            {client &&
              (client.dietitianName || client.dietitianEmail || dietitian) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <Avatar
                    src={dietitian?.profileImageUrl}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    {!dietitian?.profileImageUrl &&
                      (dietitian
                        ? `${dietitian.firstName.charAt(
                            0
                          )}${dietitian.lastName.charAt(0)}`.toUpperCase()
                        : client.dietitianName
                        ? client.dietitianName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)
                        : '👨‍⚕️')}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        display: 'block',
                        mb: 0.25,
                        fontSize: '0.75rem',
                      }}
                    >
                      Diyetisyeniniz
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: 'white', fontSize: '0.875rem' }}
                    >
                      {dietitian
                        ? `${dietitian.firstName} ${dietitian.lastName}`
                        : client.dietitianName || 'Belirtilmemiş'}
                    </Typography>
                  </Box>
                </Box>
              )}
          </Box>

          {/* Alt satır: Boy, Kilo, Yaş, Cinsiyet */}
          {client &&
            (client.height != null ||
              client.weight != null ||
              client.age != null ||
              client.gender) && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  pt: 1.5,
                }}
              >
                {client.height != null && client.height > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <HeightIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.95)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
                      Boy
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: 'white', fontSize: '0.875rem' }}>
                      {client.height} cm
                    </Typography>
                  </Box>
                )}
                {client.weight != null && client.weight > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <MonitorWeightIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.95)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
                      Kilo
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: 'white', fontSize: '0.875rem' }}>
                      {client.weight} kg
                    </Typography>
                  </Box>
                )}
                {client.age != null && client.age > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <CakeIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.95)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
                      Yaş
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: 'white', fontSize: '0.875rem' }}>
                      {client.age}
                    </Typography>
                  </Box>
                )}
                {client.gender && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.95)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
                      Cinsiyet
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: 'white', fontSize: '0.875rem' }}>
                      {client.gender}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
        </Box>
      </Paper>

      {/* Ana İstatistik Kartları */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Aktif Diyet Programı */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.alpha.primary10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AssignmentIcon
                        sx={{ fontSize: 20, color: colors.primary.main }}
                      />
                    </Box>
                    <MenuBookIcon
                      sx={{
                        opacity: 0.15,
                        fontSize: 32,
                        color: colors.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {currentProgram ? '1' : '0'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Aktif Diyet Programı
                  </Typography>
                  {currentProgram && (
                    <Chip
                      label={currentProgram.programName}
                      size="small"
                      sx={{
                        mt: 0.5,
                        background: colors.alpha.primary10,
                        color: colors.primary.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        border: `1px solid ${colors.primary.light}`,
                      }}
                    />
                  )}
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.primary.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: colors.primary.main, mb: 1.5 }}
                  >
                    Diyet Programı Detayları
                  </Typography>
                  {currentProgram ? (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: colors.text.primary, mb: 1 }}
                      >
                        {currentProgram.programName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: colors.text.secondary, mb: 1 }}
                      >
                        {currentProgram.programType}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: colors.text.secondary }}
                        >
                          İlerleme
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={stats.programProgress}
                          sx={{
                            mt: 0.5,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: colors.alpha.primary10,
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: colors.text.secondary, mt: 0.5 }}
                        >
                          %{Math.round(stats.programProgress)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: colors.text.secondary,
                      }}
                    >
                      <AssignmentIcon
                        sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Henüz diyet programı atanmamış
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>

        {/* Toplam Randevu */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.alpha.primary10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: 20, color: colors.primary.main }}
                      />
                    </Box>
                    <ScheduleIcon
                      sx={{
                        opacity: 0.15,
                        fontSize: 32,
                        color: colors.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {stats.totalAppointments}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Toplam Randevu
                  </Typography>
                  {stats.upcomingAppointments > 0 && (
                    <Chip
                      label={`${stats.upcomingAppointments} yaklaşan`}
                      size="small"
                      sx={{
                        mt: 0.5,
                        background: colors.alpha.primary10,
                        color: colors.primary.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        border: `1px solid ${colors.primary.light}`,
                      }}
                    />
                  )}
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.primary.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: colors.primary.main, mb: 1.5 }}
                  >
                    Son Randevular
                  </Typography>
                  {stats.recentAppointments.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: colors.text.secondary,
                      }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Henüz randevu bulunmuyor
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {stats.recentAppointments.map((apt) => (
                        <Box
                          key={apt.id}
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: `1px solid ${colors.border.light}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              background: colors.hover.background,
                              borderColor: colors.primary.main,
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ color: colors.text.primary }}
                          >
                            {formatDate(apt.appointmentDate)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.text.secondary }}
                          >
                            {formatTime(apt.startTime)} • {apt.status}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>

        {/* Kilo Takibi */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.alpha.primary10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MonitorWeightIcon
                        sx={{ fontSize: 20, color: colors.primary.main }}
                      />
                    </Box>
                    {stats.weightChange !== 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color:
                            stats.weightChange > 0
                              ? colors.status.error
                              : colors.status.success,
                        }}
                      >
                        {stats.weightChange > 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 28, opacity: 0.3 }} />
                        ) : (
                          <TrendingDownIcon
                            sx={{ fontSize: 28, opacity: 0.3 }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {stats.latestWeight ? `${stats.latestWeight}` : '-'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Son Ölçüm (kg)
                  </Typography>
                  {stats.weightChange !== 0 && (
                    <Chip
                      label={`${
                        stats.weightChange > 0 ? '+' : ''
                      }${stats.weightChange.toFixed(1)} kg`}
                      size="small"
                      sx={{
                        mt: 0.5,
                        background:
                          stats.weightChange > 0
                            ? 'rgba(229, 57, 53, 0.1)'
                            : colors.alpha.primary10,
                        color:
                          stats.weightChange > 0
                            ? colors.status.error
                            : colors.status.success,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        border: `1px solid ${
                          stats.weightChange > 0
                            ? 'rgba(229, 57, 53, 0.3)'
                            : colors.primary.light
                        }`,
                      }}
                    />
                  )}
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.primary.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: colors.primary.main, mb: 1.5 }}
                  >
                    Son Ölçümler
                  </Typography>
                  {stats.recentMeasurements.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: colors.text.secondary,
                      }}
                    >
                      <MonitorWeightIcon
                        sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Henüz ölçüm bulunmuyor
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {stats.recentMeasurements.map((measurement) => (
                        <Box
                          key={measurement.id}
                          onClick={() => navigate('/client/weight')}
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: `1px solid ${colors.border.light}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              background: colors.hover.background,
                              borderColor: colors.primary.main,
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ color: colors.text.primary }}
                          >
                            {measurement.weight
                              ? `${measurement.weight} kg`
                              : '-'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.text.secondary }}
                          >
                            {formatDate(measurement.measurementDate)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>

        {/* Mesajlar */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.alpha.primary10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MessageIcon
                        sx={{ fontSize: 20, color: colors.primary.main }}
                      />
                    </Box>
                    <MessageIcon
                      sx={{
                        opacity: 0.15,
                        fontSize: 32,
                        color: colors.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {unreadMessages}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Okunmamış Mesaj
                  </Typography>
                  {unreadMessages > 0 && (
                    <Chip
                      label="Yeni mesajlarınız var"
                      size="small"
                      sx={{
                        mt: 0.5,
                        background: 'rgba(244, 67, 54, 0.1)',
                        color: '#f44336',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                      }}
                    />
                  )}
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.primary.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ color: colors.primary.main }}
                    >
                      Mesajlar
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate('/client/messages')}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        borderColor: colors.primary.main,
                        color: colors.primary.main,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        width: 'fit-content',
                        px: 2,
                        py: 0.5,
                        '&:hover': {
                          borderColor: colors.primary.dark,
                          background: colors.alpha.primary10,
                          transform: 'translateX(4px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Tümünü Gör
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 3,
                      color: colors.text.secondary,
                    }}
                  >
                    <MessageIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Mesajlarınızı görüntülemek için tıklayın
                    </Typography>
                  </Box>
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Son Aktiviteler ve Hızlı Erişim */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Bugünün Öğünleri */}
        {todayMealPlan && todayMealPlan.meals.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${colors.border.light}`,
                height: '100%',
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1.5}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Bugünün Öğünleri
                  </Typography>
                  <Chip
                    label={`${todayMealPlan.meals.length} Öğün`}
                    size="small"
                    sx={{
                      bgcolor: colors.primary.light,
                      color: colors.primary.dark,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {todayMealPlan.meals.map((meal, mealIndex) => {
                    const totalCalories = meal.items.reduce((sum, item) => sum + item.calories, 0);
                    
                    return (
                      <Box
                        key={mealIndex}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: colors.background.light,
                          border: `1px solid ${colors.border.light}`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: colors.primary.light,
                            bgcolor: colors.alpha.primary10,
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <RestaurantIcon
                              sx={{ fontSize: 18, color: colors.primary.main }}
                            />
                            <Typography variant="body2" fontWeight={600}>
                              {meal.mealType}
                            </Typography>
                          </Box>
                          <Typography variant="caption" fontWeight={600} color={colors.primary.main}>
                            {totalCalories} kcal
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {meal.items.slice(0, 3).map((item, itemIndex) => (
                            <Box
                              key={itemIndex}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="caption" color="text.secondary">
                                {item.foodName} ({item.quantity} {item.unit})
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                {item.calories} kcal
                              </Typography>
                            </Box>
                          ))}
                          {meal.items.length > 3 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              +{meal.items.length - 3} besin daha...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {todayMealPlan.dailyNotes && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: colors.alpha.primary10,
                      border: `1px solid ${colors.primary.light}`,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color={colors.primary.dark} sx={{ display: 'block', mb: 0.5 }}>
                      Günlük Notlar:
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {todayMealPlan.dailyNotes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Son Yemek Kayıtları */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${colors.border.light}`,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1.5}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Son Yemek Kayıtları
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/client/nutrition')}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 0.5,
                    width: 'fit-content',
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      background: colors.alpha.primary10,
                      transform: 'translateX(4px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Tümünü Gör
                </Button>
              </Box>
              {stats.recentMeals.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 3,
                    color: colors.text.secondary,
                  }}
                >
                  <RestaurantIcon
                    sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Henüz yemek kaydı bulunmuyor
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {stats.recentMeals.map((meal) => (
                    <Box
                      key={meal.id}
                      onClick={() => navigate('/client/nutrition')}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px solid ${colors.border.light}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: colors.hover.background,
                          borderColor: colors.primary.main,
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ color: colors.text.primary }}
                          >
                            {meal.foodName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.text.secondary }}
                          >
                            {meal.mealType} • {formatDate(meal.mealDate)}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${meal.calories} kcal`}
                          size="small"
                          sx={{
                            background: colors.alpha.primary10,
                            color: colors.primary.main,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Hızlı Erişim */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${colors.border.light}`,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Hızlı Erişim
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/client/nutrition')}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                    boxShadow: `0 4px 12px ${colors.alpha.primary30}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${colors.alpha.primary20}`,
                    },
                  }}
                >
                  Günlük Kayıt Ekle
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    if (currentProgram) {
                      setProgramDetailOpen(true);
                      showInfo('Diyet programınız görüntüleniyor');
                    } else {
                      showError(
                        'Henüz size atanmış aktif bir diyet programı bulunmamaktadır.',
                        'Diyet Programı Bulunamadı'
                      );
                    }
                  }}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      background: colors.alpha.primary10,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Diyet Programı Görüntüle
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<MessageIcon />}
                  onClick={() => navigate('/client/messages')}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      background: colors.alpha.primary10,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Mesaj Gönder
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<MonitorWeightIcon />}
                  onClick={() => navigate('/client/weight')}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      background: colors.alpha.primary10,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Kilo Takibi
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bugünkü Kalori */}
      {stats.totalCaloriesToday > 0 && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${colors.border.light}`,
            mb: 3,
            background: `linear-gradient(135deg, ${colors.alpha.primary10} 0%, ${colors.alpha.primary05} 100%)`,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <RestaurantIcon
                sx={{ fontSize: 28, color: colors.primary.main }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Bugünkü Kalori Alımı
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.secondary, fontSize: '0.875rem' }}
                >
                  {stats.todayMeals?.length || 0} öğün kaydedildi
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ color: colors.primary.main, mb: 1 }}
            >
              {stats.totalCaloriesToday} kcal
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (stats.totalCaloriesToday / 2000) * 100)}
              sx={{
                height: 12,
                borderRadius: 6,
                bgcolor: colors.alpha.primary10,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 6,
                  background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: colors.text.secondary, mt: 1 }}
            >
              Hedef: 2000 kcal
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Diyet Programı Detay Modal */}
      <Dialog
        open={programDetailOpen}
        onClose={() => setProgramDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Diyet Programı Detayları</DialogTitle>
        <DialogContent>
          {currentProgram && (
            <>
              <Typography variant="h6" gutterBottom>
                {currentProgram.programName}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {currentProgram.description}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Diyet Programı Türü:</strong> {currentProgram.programType}
                </Typography>
                <Typography variant="body2">
                  <strong>Başlangıç:</strong>{' '}
                  {new Date(currentProgram.startDate).toLocaleDateString(
                    'tr-TR'
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>Bitiş:</strong>{' '}
                  {new Date(currentProgram.endDate).toLocaleDateString('tr-TR')}
                </Typography>
                {currentProgram.targetWeight && (
                  <Typography variant="body2">
                    <strong>Hedef Kilo:</strong> {currentProgram.targetWeight}{' '}
                    kg
                  </Typography>
                )}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>İlerleme:</strong>
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={stats.programProgress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 0.5 }}>
                    %{Math.round(stats.programProgress)}
                  </Typography>
                </Box>
                {currentProgram.pdfUrl && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                      Diyet Programı Dosyası
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => window.open(currentProgram.pdfUrl, '_blank')}
                        sx={{
                          bgcolor: colors.primary.main,
                          color: 'white',
                          textTransform: 'none',
                          '&:hover': {
                            bgcolor: colors.primary.dark,
                          },
                        }}
                      >
                        Dosyayı Görüntüle
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = currentProgram.pdfUrl!;
                          const pathPart = currentProgram.pdfUrl!.split('?')[0];
                          const ext = pathPart.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'pdf';
                          link.download = `${currentProgram.programName}.${ext}`;
                          link.target = '_blank';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        sx={{
                          borderColor: colors.primary.main,
                          color: colors.primary.main,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: colors.primary.dark,
                            bgcolor: colors.alpha.primary10,
                          },
                        }}
                      >
                        Dosyayı İndir
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Günlük Yemek Planları */}
                {currentProgram.dailyMealPlans && currentProgram.dailyMealPlans.length > 0 && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <DailyMealPlansView 
                      dailyMealPlans={currentProgram.dailyMealPlans} 
                      isClient={true}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={() => setProgramDetailOpen(false)}>Kapat</CancelButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientDashboard;
